export type Band = [start: number, end: number, step: number];

export interface TickInformation {
  prev: number;
  snap: number;
  next: number;
}

export function generateTicks(bands: Band[], precision: number): number[] {
  const ticks: number[] = [];
  const factor = Math.pow(10, precision);
  for (const [start, end, step] of bands) {
    let v = Math.round(start * factor);
    const endScaled = Math.round(end * factor);
    const stepScaled = Math.round(step * factor);
    while (v < endScaled) {
      ticks.push(v / factor);
      v += stepScaled;
    }
  }
  ticks.push(bands[bands.length - 1][1]);
  return [...new Set(ticks)];
}

export function snapToNearer(value: number, low: number, high: number): number {
  const distLow = Math.abs(value - low);
  const distHigh = Math.abs(high - value);
  if (distLow < distHigh) return low;
  if (distHigh < distLow) return high;
  return Math.abs(high) >= Math.abs(low) ? high : low;
}

function firstIndexAbove(value: number, ticks: number[]): number {
  let lo = 0;
  let hi = ticks.length - 1;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (ticks[mid] <= value) lo = mid + 1;
    else hi = mid;
  }
  return lo;
}

export function scanTicks(value: number, ticks: number[]): TickInformation {
  const floor = ticks[0];
  const ceil = ticks[ticks.length - 1];

  if (value <= floor) return { prev: floor, snap: floor, next: ticks[1] ?? floor };
  if (value >= ceil) return { prev: ticks[ticks.length - 2] ?? ceil, snap: ceil, next: ceil };

  const i = firstIndexAbove(value, ticks);

  if (ticks[i - 1] === value) {
    return {
      prev: ticks[i - 2] ?? ticks[i - 1],
      snap: value,
      next: ticks[i],
    };
  }

  const low = ticks[i - 1];
  const high = ticks[i];

  return { prev: low, snap: snapToNearer(value, low, high), next: high };
}
